import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  doc, getDoc, setDoc, onSnapshot,
  collection, getDocs, query, where, updateDoc
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";

// ── Design tokens ──────────────────────────────────────────────
const T = {
  bg:         "#060d1f",
  bgCard:     "rgba(10, 25, 50, 0.82)",
  teal:       "#00c9b1",
  tealDim:    "rgba(0, 201, 177, 0.18)",
  tealBorder: "rgba(0, 201, 177, 0.28)",
  text:       "#e0f7f4",
  textDim:    "rgba(160, 210, 220, 0.65)",
  header:     "linear-gradient(135deg, #0a2540 0%, #0d3060 100%)",
};

// ── 10 service categories ─────────────────────────────────────
const SUBCATEGORIES = {
  // FREE (1-5): all subcategories accessible
  "Panchayat":           ["Zilla Panchayat", "Taluk Panchayat", "Grama Panchayat"],
  "Agriculture":         ["RSK", "Departments", "Societies", "Seeds", "Fertilizers", "APMC"],
  "Milk":                ["Manmul", "Veterinary", "Feeds", "Pet Pharma"],
  "Health Care":         ["Hospitals", "Diagnostics", "Pharmacy", "Doctors"],
  "Emergency Services":  ["Police", "Ambulance", "Fire"],
  // PARTIALLY FREE (6-10): only specific subs are free, rest are locked
  "Banking & Insurance": [
    "Personal Loan", "Home Loan",                          // free (Banking A, B)
    "Life Insurance", "Term Insurance",                    // free (Insurance A, B)
    "Gold Loan", "Vehicle Loan", "Education Loan",         // locked
    "Other Banking Services",                              // locked
    "Vehicle Insurance", "Crop Insurance", "Business Insurance" // locked
  ],
  "Digital Services": [
    "Aadhaar Card", "PAN Card",                            // free (A, B)
    "Voter ID", "Online Application", "Passport",          // locked
    "Scholarship", "RTC", "Revenue Dept", "Other Services" // locked
  ],
  "District Govt Officers": [
    "DC Office", "SP Office",                              // free (A, B)
    "DHO", "DEO", "ZP CEO", "DFO", "Other District Officers" // locked
  ],
  "Taluk Govt Officers": [
    "Tahsildar", "Taluk Panchayat EO",                     // free (A, B)
    "BEO", "Agriculture Officer", "Health Inspector", "Other Taluk Officers" // locked
  ],
  "Transportation & Tourism": [
    "Bus/Train/Flight Booking", "Temple Darshana Booking", // free (A, B)
    "Domestic Tour Packages", "International Tour Packages"// locked
  ],
};

// Which subcategories are free in the partially-premium categories
const FREE_SUBS = {
  "Banking & Insurance":   new Set(["Personal Loan", "Home Loan", "Life Insurance", "Term Insurance"]),
  "Digital Services":      new Set(["Aadhaar Card", "PAN Card"]),
  "District Govt Officers":new Set(["DC Office", "SP Office"]),
  "Taluk Govt Officers":   new Set(["Tahsildar", "Taluk Panchayat EO"]),
  "Transportation & Tourism": new Set(["Bus/Train/Flight Booking", "Temple Darshana Booking"]),
};

function checkLocked(categoryName, subName, isPremium) {
  if (isPremium) return false;
  if (!FREE_SUBS[categoryName]) return false; // categories 1-5 fully free
  return !FREE_SUBS[categoryName].has(subName);
}

const ICONS = {
  "Panchayat": "🏛️",
  "Agriculture": "🚜",
  "Milk": "🐄",
  "Health Care": "🏥",
  "Emergency Services": "🚨",
  "Banking & Insurance": "💳",
  "Digital Services": "💻",
  "District Govt Officers": "🏢",
  "Taluk Govt Officers": "🏛",
  "Transportation & Tourism": "🚌",
};

// Kannada translations
const KN = {
  "Panchayat": "ಪಂಚಾಯಿತಿಗಳು",
  "Agriculture": "ಕೃಷಿ",
  "Milk": "ಹೈನುಗಾರಿಕೆ",
  "Health Care": "ಆರೋಗ್ಯ",
  "Emergency Services": "ತುರ್ತು ಸೇವೆಗಳು",
  "Banking & Insurance": "ಬ್ಯಾಂಕಿಂಗ್ ಮತ್ತು ವಿಮೆ",
  "Digital Services": "ಡಿಜಿಟಲ್ ಸೇವೆಗಳು",
  "District Govt Officers": "ಸರ್ಕಾರಿ ಇಲಾಖೆಗಳ ಜಿಲ್ಲಾ ಮಟ್ಟದ ಅಧಿಕಾರಿಗಳು",
  "Taluk Govt Officers": "ಸರ್ಕಾರಿ ಇಲಾಖೆಗಳ ತಾಲ್ಲೂಕು ಮಟ್ಟದ ಅಧಿಕಾರಿಗಳು",
  "Transportation & Tourism": "ಸಾರಿಗೆ ಮತ್ತು ಪ್ರವಾಸೋದ್ಯಮ",
  // subcategory translations
  "Zilla Panchayat": "ಜಿಲ್ಲಾ ಪಂಚಾಯತ್",
  "Taluk Panchayat": "ತಾಲ್ಲೂಕು ಪಂಚಾಯತ್",
  "Grama Panchayat": "ಗ್ರಾಮ ಪಂಚಾಯತ್",
  "RSK": "ಆರ್‌ಎಸ್‌ಕೆ",
  "Departments": "ವಿಭಾಗಗಳು",
  "Societies": "ಸಂಸ್ಥೆಗಳು",
  "Seeds": "ಬೀಜಗಳು",
  "Fertilizers": "ರಸಗೊಬ್ಬರಗಳು",
  "APMC": "ಎಪಿಎಂಸಿ",
  "Manmul": "ಮ್ಯಾನ್‌ಮುಲ್",
  "Veterinary": "ಪಶುವೈದ್ಯಕೀಯ",
  "Feeds": "ಮೇವು",
  "Pet Pharma": "ಪೆಟ್ ಫಾರ್ಮಾ",
  "Hospitals": "ಆಸ್ಪತ್ರೆಗಳು",
  "Diagnostics": "ಡಯಾಗ್ನೋಸ್ಟಿಕ್ಸ್",
  "Pharmacy": "ಔಷಧ ಅಂಗಡಿ",
  "Doctors": "ವೈದ್ಯರು",
  "Police": "ಪೊಲೀಸ್",
  "Ambulance": "ಆಂಬ್ಯುಲೆನ್ಸ್",
  "Fire": "ಅಗ್ನಿಶಾಮಕ",
  "Personal Loan": "ವೈಯಕ್ತಿಕ ಸಾಲ",
  "Home Loan": "ಗೃಹ ಸಾಲ",
  "Life Insurance": "ಜೀವ ವಿಮೆ",
  "Term Insurance": "ಅವಧಿ ವಿಮೆ",
  "Gold Loan": "ಚಿನ್ನದ ಸಾಲ",
  "Vehicle Loan": "ವಾಹನ ಸಾಲ",
  "Education Loan": "ಶಿಕ್ಷಣ ಸಾಲ",
  "Other Banking Services": "ಇತರ ಬ್ಯಾಂಕಿಂಗ್ ಸೇವೆಗಳು",
  "Vehicle Insurance": "ವಾಹನ ವಿಮೆ",
  "Crop Insurance": "ಬೆಳೆ ವಿಮೆ",
  "Business Insurance": "ವ್ಯಾಪಾರ ವಿಮೆ",
  "Aadhaar Card": "ಆಧಾರ್ ಕಾರ್ಡ್",
  "PAN Card": "ಪ್ಯಾನ್ ಕಾರ್ಡ್",
  "Voter ID": "ಮತದಾರ ಗುರುತು",
  "Online Application": "ಆನ್‌ಲೈನ್ ಅರ್ಜಿ",
  "Passport": "ಪಾಸ್‌ಪೋರ್ಟ್",
  "Scholarship": "ವಿದ್ಯಾರ್ಥಿವೇತನ",
  "RTC": "ಆರ್‌ಟಿಸಿ",
  "Revenue Dept": "ರೆವೆನ್ಯೂ ಇಲಾಖೆ",
  "Other Services": "ಇತರ ಸೇವೆಗಳು",
  "DC Office": "ಡಿಸಿ ಕಚೇರಿ",
  "SP Office": "ಎಸ್ಪಿ ಕಚೇರಿ",
  "DHO": "ಜಿಲ್ಲಾ ಆರೋಗ್ಯ ಅಧಿಕಾರಿ",
  "DEO": "ಜಿಲ್ಲಾ ಶಿಕ್ಷಣ ಅಧಿಕಾರಿ",
  "ZP CEO": "ಜಿ.ಪಂ. ಸಿಇಒ",
  "DFO": "ಡಿಎಫ್‌ಒ",
  "Other District Officers": "ಇತರ ಜಿಲ್ಲಾ ಅಧಿಕಾರಿಗಳು",
  "Tahsildar": "ತಹಶೀಲ್ದಾರ್",
  "Taluk Panchayat EO": "ತಾ.ಪಂ. ಇಒ",
  "BEO": "ಬಿಇಒ",
  "Agriculture Officer": "ಕೃಷಿ ಅಧಿಕಾರಿ",
  "Health Inspector": "ಆರೋಗ್ಯ ನಿರೀಕ್ಷಕ",
  "Other Taluk Officers": "ಇತರ ತಾಲ್ಲೂಕು ಅಧಿಕಾರಿಗಳು",
  "Bus/Train/Flight Booking": "ಬಸ್/ರೈಲು/ವಿಮಾನ ಬುಕ್ಕಿಂಗ್",
  "Temple Darshana Booking": "ದೇವಾಲಯ ದರ್ಶನ ಬುಕ್ಕಿಂಗ್",
  "Domestic Tour Packages": "ದೇಶೀಯ ಪ್ರವಾಸ ಪ್ಯಾಕೇಜ್",
  "International Tour Packages": "ಅಂತಾರಾಷ್ಟ್ರೀಯ ಪ್ರವಾಸ ಪ್ಯಾಕೇಜ್",
};

export default function Dashboard({ lang, setLang, setUser }) {
  const navigate = useNavigate();

  const [loading, setLoading]                   = useState(true);
  const [district, setDistrict]                 = useState("Mandya");
  const [showDistrictDD, setShowDistrictDD]     = useState(false);
  const [userData, setUserData]                 = useState(null);
  const [isPremium, setIsPremium]               = useState(false);
  const [paymentStatus, setPaymentStatus]       = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [queryText, setQueryText]               = useState("");
  const [subList, setSubList]                   = useState([]);
  const [vendors, setVendors]                   = useState([]);
  const [manualEntries, setManualEntries]       = useState([]);
  const [vendorLoading, setVendorLoading]       = useState(false);

  // Premium modal states
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumPending, setPremiumPending]     = useState(false);
  const [premiumPayLoading, setPremiumPayLoading] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      sessionStorage.clear();
      navigate("/");
    } catch (err) {
      alert("Logout failed");
    }
  };

  const districts = [
    "Mandya", "Maddur", "Malavalli", "Srirangapatna",
    "Nagamangala", "Krishnarajapete", "Pandavapura"
  ];

  const districtKN = {
    Mandya: "ಮಂಡ್ಯ", Maddur: "ಮದ್ದೂರು", Malavalli: "ಮಳವಳ್ಳಿ",
    Srirangapatna: "ಶ್ರೀರಂಗಪಟ್ಟಣ", Nagamangala: "ನಾಗಮಂಗಲ",
    Krishnarajapete: "ಕೃಷ್ಣರಾಜಪೇಟೆ", Pandavapura: "ಪಾಂಡವಪುರ"
  };

  const tr = (t) => lang === "kn" && KN[t] ? KN[t] : t;

  const text = {
    en: { hello: "Hello", back: "← Back", ask: "Ask us anything...", submit: "Submit", categories: "Categories", vendors: "Service Providers", noVendors: "No service providers found in this area.", askTitle: "Have a Question?" },
    kn: { hello: "ನಮಸ್ಕಾರ", back: "← ಹಿಂದಕ್ಕೆ", ask: "ಯಾವುದೇ ಪ್ರಶ್ನೆ ಕೇಳಿ...", submit: "ಸಲ್ಲಿಸಿ", categories: "ವರ್ಗಗಳು", vendors: "ಸೇವಾ ಪೂರೈಕೆದಾರರು", noVendors: "ಈ ಪ್ರದೇಶದಲ್ಲಿ ಯಾವುದೇ ಸೇವಾ ಪೂರೈಕೆದಾರರು ಕಂಡುಬಂದಿಲ್ಲ.", askTitle: "ಪ್ರಶ್ನೆ ಇದೆಯೇ?" }
  };
  const t = text[lang];
  const categories = Object.keys(SUBCATEGORIES);

  // ── Auth + real-time user listener (premium status updates instantly) ──
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("user"));
      if (cached && (cached.name || cached.email)) setUserData(cached);
    } catch (e) {}

    let unsubUser = () => {};

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) { window.location.href = "/"; return; }
      setUser(user);

      try {
        const vendorSnap = await getDoc(doc(db, "vendors", user.uid));
        if (vendorSnap.exists()) { window.location.href = "/"; return; }

        // Real-time listener so premium status unlocks without re-login
        unsubUser = onSnapshot(doc(db, "users", user.uid), (snap) => {
          if (!snap.exists()) { window.location.href = "/"; return; }
          const data = snap.data();
          setUserData(data);
          const premium = data.isPremium === true && data.paymentStatus === "approved";
          setIsPremium(premium);
          setPaymentStatus(data.paymentStatus || "");
          if (premium) {
            setShowPremiumModal(false);
            setPremiumPending(false);
          }
          localStorage.setItem("user", JSON.stringify(data));
          setLoading(false);
        });
      } catch (err) {
        console.warn("Dashboard: could not fetch user doc", err);
        setLoading(false);
      }
    });

    return () => {
      unsubAuth();
      unsubUser();
    };
  }, []);

  useEffect(() => {
    if (!selectedSubcategory) return;
    fetchVendors(selectedSubcategory);
  }, [selectedSubcategory, district]);

  const fetchVendors = async (subcategory) => {
    setVendorLoading(true);
    try {
      const vendorSnap = await getDocs(
        query(collection(db, "vendors"),
          where("approvalStatus", "==", "approved"),
          where("subcategory", "==", subcategory)
        )
      );
      const filtered = vendorSnap.docs
        .map(d => ({ ...d.data(), id: d.id, type: "vendor" }))
        .filter(v => !district || (v.subdistrict || "").toLowerCase() === district.toLowerCase());
      setVendors(filtered);

      const manualSnap = await getDocs(
        query(collection(db, "manual_entries"), where("subcategory", "==", subcategory))
      );
      const manualFiltered = manualSnap.docs
        .map(d => ({ ...d.data(), id: d.id, type: "manual" }))
        .filter(m => !district || !m.subdistrict || m.subdistrict.toLowerCase() === district.toLowerCase());
      setManualEntries(manualFiltered);
    } catch (err) {
      console.error("Fetch error:", err);
      setVendors([]);
      setManualEntries([]);
    }
    setVendorLoading(false);
  };

  const handleSubmitQuery = async () => {
    if (!queryText.trim()) return;
    await setDoc(doc(db, "queries", Date.now().toString()), {
      uid: userData?.uid,
      name: userData?.name,
      question: queryText,
      createdAt: new Date()
    });
    setQueryText("");
    alert("Submitted!");
  };

  // ── Premium modal: Pay Now ──────────────────────────────────
  const handlePremiumPay = async () => {
    try {
      setPremiumPayLoading(true);
      await updateDoc(doc(db, "users", userData.uid), { paymentStatus: "pending" });
      setPremiumPending(true);
    } catch (err) {
      console.error(err);
      alert("Failed to submit. Try again.");
    } finally {
      setPremiumPayLoading(false);
    }
  };

  const handleSubcategoryTap = (catName, subName) => {
    if (checkLocked(catName, subName, isPremium)) {
      setShowPremiumModal(true);
      setPremiumPending(paymentStatus === "pending");
      return;
    }
    setSelectedSubcategory(subName);
    setVendors([]);
    setManualEntries([]);
  };

  // ── Sub-components ─────────────────────────────────────────

  function CategoryCard({ name }) {
    const hasPremiumContent = !!FREE_SUBS[name];
    return (
      <div
        onClick={() => {
          setSelectedCategory(name);
          setSubList(SUBCATEGORIES[name] || []);
          setSelectedSubcategory(null);
          setVendors([]);
          setManualEntries([]);
        }}
        style={{
          width: "48%", minHeight: 110, borderRadius: 18,
          background: T.bgCard,
          border: `1px solid ${T.tealBorder}`,
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", marginBottom: 14, cursor: "pointer",
          boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
          position: "relative",
          transition: "transform 0.15s, box-shadow 0.15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 12px 30px rgba(0,0,0,0.5), 0 0 16px ${T.tealDim}`; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.35)"; }}
      >
        {hasPremiumContent && !isPremium && (
          <div style={{
            position: "absolute", top: 6, right: 8,
            fontSize: 10, color: "#ffaa00",
            background: "rgba(255,170,0,0.15)",
            border: "1px solid rgba(255,170,0,0.3)",
            borderRadius: 6, padding: "2px 6px", fontWeight: 700
          }}>Partial 🔒</div>
        )}
        <div style={{ fontSize: 30, marginBottom: 6 }}>{ICONS[name] || "📋"}</div>
        <div style={{ fontWeight: 600, color: T.text, textAlign: "center", padding: "0 8px", fontSize: 13 }}>
          {tr(name)}
        </div>
      </div>
    );
  }

  function SubcategoryCard({ name, catName }) {
    const locked = checkLocked(catName, name, isPremium);
    const active = selectedSubcategory === name;
    return (
      <div
        onClick={() => handleSubcategoryTap(catName, name)}
        style={{
          width: "48%", minHeight: 80, borderRadius: 18,
          background: locked
            ? "rgba(10, 20, 40, 0.7)"
            : active
              ? "linear-gradient(135deg, #00c9b1, #0077cc)"
              : T.bgCard,
          border: locked
            ? "1px solid rgba(255,170,0,0.2)"
            : `1px solid ${active ? "transparent" : T.tealBorder}`,
          color: locked ? "rgba(160,200,210,0.45)" : active ? "#fff" : T.text,
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center",
          marginBottom: 14, cursor: "pointer",
          boxShadow: active ? "0 8px 24px rgba(0,201,177,0.35)" : "0 8px 24px rgba(0,0,0,0.3)",
          fontWeight: 600, textAlign: "center", padding: "10px 8px",
          fontSize: 13, transition: "all 0.15s",
        }}
      >
        {locked && <div style={{ fontSize: 16, marginBottom: 4 }}>🔒</div>}
        <div>{tr(name)}</div>
        {locked && (
          <div style={{
            marginTop: 4, fontSize: 10, fontWeight: 700,
            color: "#ffaa00",
            background: "rgba(255,170,0,0.15)",
            border: "1px solid rgba(255,170,0,0.3)",
            borderRadius: 6, padding: "1px 6px"
          }}>Premium</div>
        )}
      </div>
    );
  }

  function VendorCard({ vendor }) {
    const photo = vendor.imageUrl || vendor.image || vendor.photoURL;
    const initial = (vendor.name || vendor.shopName || "V").trim().charAt(0).toUpperCase();
    return (
      <div style={{
        background: T.bgCard, borderRadius: 16, padding: 16, marginBottom: 12,
        border: `1px solid ${T.tealBorder}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.4)", display: "flex", gap: 14
      }}>
        {photo ? (
          <img src={photo} alt={vendor.name}
            onError={e => { e.currentTarget.style.display = "none"; if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex"; }}
            style={{ width: 58, height: 58, borderRadius: "50%", objectFit: "cover", border: `2px solid ${T.teal}` }} />
        ) : null}
        <div style={{
          width: 58, height: 58, borderRadius: "50%",
          background: "linear-gradient(135deg, #00c9b1, #0077cc)",
          display: photo ? "none" : "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 800, fontSize: 22,
          border: `2px solid ${T.teal}`, flexShrink: 0,
        }}>{initial}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: T.text }}>{vendor.name || vendor.shopName}</div>
          {vendor.shopName && vendor.name !== vendor.shopName && (
            <div style={{ color: T.textDim, fontSize: 13 }}>🏢 {vendor.shopName}</div>
          )}
          {vendor.phone    && <div style={{ color: T.textDim, fontSize: 13 }}>📞 {vendor.phone}</div>}
          {vendor.address  && <div style={{ color: T.textDim, fontSize: 13 }}>📍 {vendor.address}</div>}
          {vendor.subdistrict && <div style={{ color: T.textDim, fontSize: 12 }}>🗺 {vendor.subdistrict}</div>}
        </div>
        <div style={{
          background: "rgba(0,201,177,0.15)", color: T.teal, borderRadius: 10,
          padding: "4px 10px", fontSize: 11, fontWeight: 700, height: "fit-content",
          border: `1px solid ${T.tealBorder}`
        }}>✔ Verified</div>
      </div>
    );
  }

  function ManualEntryCard({ entry }) {
    return (
      <div style={{
        background: "rgba(20, 35, 60, 0.9)", borderRadius: 16, padding: 16, marginBottom: 12,
        border: "1px solid rgba(255, 200, 50, 0.25)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.4)"
      }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: T.text }}>{entry.name}</div>
        {entry.phone && <div style={{ color: T.textDim, fontSize: 13 }}>📞 {entry.phone}</div>}
        {entry.address && <div style={{ color: T.textDim, fontSize: 13 }}>📍 {entry.address}</div>}
        {entry.description && <div style={{ color: T.textDim, fontSize: 13, marginTop: 4 }}>{entry.description}</div>}
        {entry.website && (
          <a href={entry.website} target="_blank" rel="noreferrer" style={{ color: T.teal, fontSize: 13 }}>
            🌐 Website
          </a>
        )}
      </div>
    );
  }

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: T.bg, color: T.text }}>
      Loading...
    </div>
  );

  const allResults = [...vendors, ...manualEntries];

  return (
    <div style={{ padding: 15, background: T.bg, minHeight: "100vh" }} onClick={() => setShowDistrictDD(false)}>

      {/* ── PREMIUM MODAL ─────────────────────────────────── */}
      {showPremiumModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999, padding: 20
        }}>
          <div style={{
            background: "linear-gradient(135deg, #0a2540, #0d3060)",
            border: `1px solid ${T.tealBorder}`,
            borderRadius: 24, padding: 28, maxWidth: 340, width: "100%",
            boxShadow: "0 24px 60px rgba(0,0,0,0.7), 0 0 40px rgba(0,201,177,0.1)",
            textAlign: "center"
          }}>
            {premiumPending ? (
              <>
                <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
                <h3 style={{ color: T.text, margin: "0 0 8px" }}>Payment Request Sent</h3>
                <p style={{ color: T.textDim, fontSize: 14 }}>
                  Waiting for admin approval. You'll be notified once premium is activated.
                </p>
                <div style={{
                  width: 40, height: 40, border: `3px solid ${T.teal}`,
                  borderTopColor: "transparent", borderRadius: "50%",
                  animation: "spin 1s linear infinite", margin: "20px auto"
                }} />
                <p style={{ color: "#ffaa00", fontSize: 12 }}>
                  Keep this page open for instant unlock when approved.
                </p>
                <button
                  onClick={() => setShowPremiumModal(false)}
                  style={{
                    marginTop: 16, padding: "10px 20px", background: "transparent",
                    color: T.textDim, border: `1px solid ${T.tealBorder}`,
                    borderRadius: 10, cursor: "pointer", fontSize: 13
                  }}
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 48, marginBottom: 8 }}>🔒</div>
                <h3 style={{ color: T.teal, margin: "0 0 4px", fontSize: 20 }}>Premium Content</h3>
                <p style={{ color: T.textDim, fontSize: 13, margin: "0 0 16px" }}>
                  Upgrade to access all services
                </p>

                <div style={{
                  background: "rgba(0,201,177,0.08)", border: `1px solid ${T.tealBorder}`,
                  borderRadius: 16, padding: 16, marginBottom: 18
                }}>
                  <div style={{ color: T.teal, fontSize: 28, fontWeight: 800 }}>₹99</div>
                  <div style={{ color: T.textDim, fontSize: 13 }}>per month</div>
                  <div style={{ marginTop: 12, textAlign: "left" }}>
                    {["All service categories unlocked", "Real-time government contacts", "Premium tour packages", "Digital services access"].map((f, i) => (
                      <div key={i} style={{ color: T.text, fontSize: 13, marginBottom: 6 }}>
                        ✔ {f}
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handlePremiumPay}
                  disabled={premiumPayLoading}
                  style={{
                    width: "100%", padding: "14px",
                    background: "linear-gradient(135deg, #00c9b1, #0077cc)",
                    color: "#fff", border: "none", borderRadius: 14,
                    fontWeight: 700, fontSize: 16, cursor: "pointer",
                    boxShadow: "0 6px 20px rgba(0,201,177,0.4)",
                    marginBottom: 10
                  }}
                >
                  {premiumPayLoading ? "Processing..." : "Pay Now — ₹99/month"}
                </button>

                <button
                  onClick={() => setShowPremiumModal(false)}
                  style={{
                    width: "100%", padding: "10px",
                    background: "transparent", color: T.textDim,
                    border: `1px solid ${T.tealBorder}`, borderRadius: 10,
                    cursor: "pointer", fontSize: 13
                  }}
                >
                  Maybe Later
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── HEADER ─────────────────────────────────────────── */}
      <div style={{
        background: T.header, border: `1px solid ${T.tealBorder}`,
        padding: "14px 16px", borderRadius: 20, color: T.text,
        marginBottom: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.4)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: T.text }}>
            {t.hello}, <span style={{ color: T.teal }}>{userData?.name || userData?.email || "User"}</span>
            {isPremium && (
              <span style={{
                marginLeft: 8, fontSize: 11, background: "linear-gradient(135deg, #ffaa00, #ff8800)",
                color: "#fff", padding: "2px 8px", borderRadius: 8, fontWeight: 700
              }}>⭐ Premium</span>
            )}
          </h3>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button onClick={handleLogout} style={{
              background: "rgba(0,201,177,0.15)", color: T.teal,
              border: `1px solid ${T.tealBorder}`, padding: "6px 14px",
              borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 13
            }}>Logout</button>

            {userData?.photoURL ? (
              <img src={userData.photoURL} onClick={() => navigate("/profile")}
                onError={e => { e.currentTarget.style.display = "none"; if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex"; }}
                style={{ width: 40, height: 40, borderRadius: "50%", cursor: "pointer", objectFit: "cover", border: `2px solid ${T.teal}` }}
                alt="profile" />
            ) : null}
            <div onClick={() => navigate("/profile")} style={{
              width: 40, height: 40, borderRadius: "50%", cursor: "pointer",
              background: "linear-gradient(135deg, #00c9b1, #0077cc)",
              display: userData?.photoURL ? "none" : "flex",
              alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 800, fontSize: 16, border: `2px solid ${T.teal}`,
            }}>
              {(userData?.name || userData?.email || "U").trim().charAt(0).toUpperCase()}
            </div>
          </div>
        </div>

        {/* District + Language row */}
        <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center", position: "relative", zIndex: showDistrictDD ? 500 : 1 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <div onClick={(e) => { e.stopPropagation(); setShowDistrictDD(!showDistrictDD); }} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 16px", borderRadius: 25,
              background: "rgba(0,201,177,0.1)", border: `1px solid ${T.tealBorder}`,
              cursor: "pointer", color: T.text, fontWeight: 600, fontSize: 14,
            }}>
              <span>🗺️ {lang === "kn" ? districtKN[district] : district}</span>
              <span style={{ color: T.teal, fontSize: 18 }}>{showDistrictDD ? "▲" : "▼"}</span>
            </div>
            {showDistrictDD && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
                background: "#0d2040", border: `1px solid ${T.tealBorder}`,
                borderRadius: 16, boxShadow: "0 16px 40px rgba(0,0,0,0.7)",
                zIndex: 9999, overflow: "hidden", maxHeight: 240, overflowY: "auto",
              }}>
                {districts.map((d, i) => (
                  <div key={i} onClick={() => { setDistrict(d); setShowDistrictDD(false); }}
                    style={{
                      padding: "12px 16px", cursor: "pointer",
                      color: d === district ? T.teal : T.text,
                      fontWeight: d === district ? 700 : 400,
                      background: d === district ? "rgba(0,201,177,0.12)" : "transparent",
                      borderBottom: i < districts.length - 1 ? `1px solid rgba(0,201,177,0.1)` : "none",
                      fontSize: 14,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(0,201,177,0.15)"}
                    onMouseLeave={e => e.currentTarget.style.background = d === district ? "rgba(0,201,177,0.12)" : "transparent"}
                  >
                    {lang === "kn" ? districtKN[d] : d}{d === district && <span style={{ marginLeft: 8, fontSize: 12 }}>✓</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => setLang(lang === "en" ? "kn" : "en")} style={{
            background: "rgba(0,201,177,0.1)", color: T.teal,
            border: `1px solid ${T.tealBorder}`, padding: "10px 16px",
            borderRadius: 25, cursor: "pointer", fontWeight: 700, fontSize: 13,
            whiteSpace: "nowrap", flexShrink: 0
          }}>
            {lang === "en" ? "ಕನ್ನಡ" : "EN"}
          </button>
        </div>
      </div>

      {/* ── BREADCRUMB ─────────────────────────────────────── */}
      {selectedSubcategory && (
        <button onClick={() => { setSelectedSubcategory(null); setVendors([]); setManualEntries([]); }}
          style={{ marginBottom: 10, background: "none", border: "none", color: T.teal, fontWeight: 600, fontSize: 15, cursor: "pointer" }}>
          ← {tr(selectedCategory)}
        </button>
      )}
      {selectedCategory && !selectedSubcategory && (
        <button onClick={() => { setSelectedCategory(null); setSubList([]); }}
          style={{ marginBottom: 10, background: "none", border: "none", color: T.teal, fontWeight: 600, fontSize: 15, cursor: "pointer" }}>
          {t.back}
        </button>
      )}

      {/* ── TITLE ──────────────────────────────────────────── */}
      <h3 style={{ marginBottom: 14, color: T.text, fontWeight: 700 }}>
        {selectedSubcategory
          ? `${tr(selectedSubcategory)} — ${t.vendors}`
          : selectedCategory ? tr(selectedCategory) : t.categories}
      </h3>

      {/* ── CATEGORY GRID ─────────────────────────────────── */}
      {!selectedCategory && (
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}>
          {categories.map((cat, i) => <CategoryCard key={i} name={cat} />)}
        </div>
      )}

      {/* ── SUBCATEGORY GRID ──────────────────────────────── */}
      {selectedCategory && !selectedSubcategory && (
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}>
          {subList.map((item, i) => <SubcategoryCard key={i} name={item} catName={selectedCategory} />)}
        </div>
      )}

      {/* ── VENDOR LIST ───────────────────────────────────── */}
      {selectedSubcategory && (
        <div>
          {vendorLoading ? (
            <div style={{ textAlign: "center", padding: 30, color: T.textDim }}>Loading...</div>
          ) : allResults.length === 0 ? (
            <div style={{
              background: T.bgCard, borderRadius: 16, padding: 30, textAlign: "center",
              color: T.textDim, border: `1px solid ${T.tealBorder}`, boxShadow: "0 4px 20px rgba(0,0,0,0.35)"
            }}>
              <div style={{ fontSize: 40 }}>🔍</div>
              <p>{t.noVendors}</p>
            </div>
          ) : (
            allResults.map((item, i) =>
              item.type === "manual"
                ? <ManualEntryCard key={`m-${item.id || i}`} entry={item} />
                : <VendorCard key={`v-${item.id || i}`} vendor={item} />
            )
          )}
        </div>
      )}

      {/* ── ASK ANYTHING ──────────────────────────────────── */}
      <div style={{
        marginTop: 28, background: T.bgCard, border: `1px solid ${T.tealBorder}`,
        borderRadius: 24, padding: "22px 18px", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            background: "linear-gradient(135deg, #00c9b1, #0077cc)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, flexShrink: 0
          }}>💬</div>
          <div>
            <div style={{ fontWeight: 700, color: T.text, fontSize: 16 }}>{t.askTitle}</div>
            <div style={{ color: T.textDim, fontSize: 12 }}>
              {lang === "en" ? "We'll get back to you shortly." : "ನಾವು ಶೀಘ್ರದಲ್ಲೇ ಉತ್ತರಿಸುತ್ತೇವೆ."}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <input
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            placeholder={t.ask}
            onKeyDown={(e) => e.key === "Enter" && handleSubmitQuery()}
            style={{
              flex: 1, padding: "12px 18px", borderRadius: 25,
              border: `1px solid ${T.tealBorder}`,
              background: "rgba(0,201,177,0.07)", color: T.text,
              outline: "none", fontSize: 14,
            }}
          />
          <button onClick={handleSubmitQuery} style={{
            background: "linear-gradient(135deg, #00c9b1, #0077cc)",
            color: "#fff", border: "none", padding: "12px 20px",
            borderRadius: 25, fontWeight: 700, cursor: "pointer", fontSize: 14,
            flexShrink: 0, boxShadow: "0 4px 16px rgba(0,201,177,0.35)"
          }}>{t.submit}</button>
        </div>
      </div>

      <div style={{ height: 30 }} />

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
